"use client";

import { useState, useMemo } from "react";
import {
  HiMail,
  HiQuestionMarkCircle,
  HiCheckCircle,
  HiXCircle,
  HiExclamation,
} from "@/components/icons";
import ToolsCustomDevCTA from "@/components/ToolsCustomDevCTA";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ToolPageSchema from "@/components/schemas/ToolPageSchema";
import UnifiedFAQ from "@/components/UnifiedFAQ";

const SPAM_WORDS = [
  "FREE",
  "FREE!",
  "URGENT",
  "ACT NOW",
  "ACT NOW!",
  "CLICK HERE",
  "CLICK HERE!",
  "BUY NOW",
  "ORDER NOW",
  "LIMITED TIME",
  "LIMITED OFFER",
  "GUARANTEED",
  "WINNER",
  "CONGRATULATIONS",
  "PRIZE",
  "CASH",
  "EARN MONEY",
  "MAKE MONEY",
  "EXTRA INCOME",
  "NO COST",
  "NO FEES",
  "NO OBLIGATION",
  "RISK FREE",
  "100%",
  "SAVE BIG",
  "AMAZING",
  "INCREDIBLE",
  "BEST PRICE",
  "LOWEST PRICE",
  "EXCLUSIVE DEAL",
  "SPECIAL PROMOTION",
  "UNSUBSCRIBE",
  "OPT OUT",
  "THIS IS NOT SPAM",
  "NOT JUNK",
];

const MOBILE_PREVIEW_LENGTH = 40;
const IDEAL_MIN = 30;
const IDEAL_MAX = 50;

function containsEmoji(str: string): boolean {
  const emojiRegex =
    /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE0F}]/u;
  return emojiRegex.test(str);
}

function countEmojis(str: string): number {
  const emojiRegex =
    /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE0F}]/gu;
  return (str.match(emojiRegex) || []).length;
}

function analyzeSubject(subject: string) {
  const len = subject.length;
  const trimmed = subject.trim();
  const upper = trimmed.toUpperCase();

  // Spam word detection
  const foundSpamWords = SPAM_WORDS.filter((word) => upper.includes(word));

  // All caps detection (ignoring short strings)
  const words = trimmed.split(/\s+/).filter(Boolean);
  const allCapsWords = words.filter(
    (w) => w.length > 2 && w === w.toUpperCase() && /[A-Z]/.test(w)
  );
  const hasAllCaps = allCapsWords.length > 0;

  // Emoji info
  const hasEmoji = containsEmoji(trimmed);
  const emojiCount = countEmojis(trimmed);

  // Excessive punctuation
  const excessivePunctuation = (trimmed.match(/[!?]{2,}/g) || []).length > 0;
  const exclamationCount = (trimmed.match(/!/g) || []).length;

  // Score calculation (start at 100, deduct)
  let score = 100;

  // Length deductions
  if (len === 0) {
    score = 0;
  } else if (len < 10) {
    score -= 30;
  } else if (len < IDEAL_MIN) {
    score -= 10;
  } else if (len > 70) {
    score -= 20;
  } else if (len > IDEAL_MAX) {
    score -= 5;
  }

  // Spam word deductions (up to 50)
  score -= Math.min(foundSpamWords.length * 15, 50);

  // All caps deductions
  if (hasAllCaps) score -= 15;

  // Excessive punctuation
  if (excessivePunctuation) score -= 10;
  if (exclamationCount > 1) score -= 5;

  // Emoji: 1 is okay, more than 2 deduct
  if (emojiCount > 2) score -= 10;

  score = Math.max(0, Math.min(100, score));

  // Mobile preview
  const mobilePreview =
    trimmed.length > MOBILE_PREVIEW_LENGTH
      ? trimmed.slice(0, MOBILE_PREVIEW_LENGTH) + "…"
      : trimmed;

  const isTruncated = trimmed.length > MOBILE_PREVIEW_LENGTH;

  return {
    len,
    score,
    foundSpamWords,
    hasEmoji,
    emojiCount,
    hasAllCaps,
    allCapsWords,
    excessivePunctuation,
    exclamationCount,
    mobilePreview,
    isTruncated,
    isIdealLength: len >= IDEAL_MIN && len <= IDEAL_MAX,
  };
}

function ScoreRing({ score }: { score: number }) {
  const color = score >= 75 ? "text-green-500" : score >= 50 ? "text-yellow-500" : "text-red-500";
  const label = score >= 75 ? "Good" : score >= 50 ? "Fair" : score >= 25 ? "Needs Work" : "Poor";

  return (
    <div className="flex flex-col items-center gap-1">
      <div className={`text-6xl font-black ${color}`}>{score}</div>
      <div className="text-sm text-muted-foreground font-medium">/ 100</div>
      <div className={`text-sm font-bold ${color}`}>{label}</div>
    </div>
  );
}

function CheckItem({ pass, warn, label }: { pass: boolean; warn?: boolean; label: string }) {
  if (pass) {
    return (
      <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
        <HiCheckCircle className="w-4 h-4 flex-shrink-0" />
        <span>{label}</span>
      </div>
    );
  }
  if (warn) {
    return (
      <div className="flex items-center gap-2 text-sm text-yellow-600 dark:text-yellow-400">
        <HiExclamation className="w-4 h-4 flex-shrink-0" />
        <span>{label}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
      <HiXCircle className="w-4 h-4 flex-shrink-0" />
      <span>{label}</span>
    </div>
  );
}

const faqItems = [
  {
    question: "What makes a good email subject line?",
    answer:
      "A good email subject line is 30–50 characters long (to avoid truncation on mobile), creates curiosity or urgency without being spammy, avoids all-caps words and excessive punctuation, and is directly relevant to the email content. Personalization and specificity also tend to improve open rates.",
  },
  {
    question: "Which spam words should I avoid?",
    answer:
      "Common spam trigger words include: FREE, URGENT, ACT NOW, CLICK HERE, BUY NOW, GUARANTEED, WINNER, CASH, EARN MONEY, RISK FREE, 100% FREE, and phrases like LIMITED TIME OFFER. These words flag your email for spam filters and can significantly reduce deliverability.",
  },
  {
    question: "How many characters should my subject line be?",
    answer:
      "Aim for 30–50 characters for the best results across all devices. Most desktop email clients show 60–70 characters, while mobile clients often truncate at 40 characters. Keep your key message in the first 40 characters so it's always visible on mobile screens.",
  },
  {
    question: "Are emojis in subject lines a good idea?",
    answer:
      "One emoji can boost open rates by making your email stand out in the inbox. However, more than two emojis starts to look spammy and can trigger spam filters. Also ensure emojis are relevant to your content and display correctly across email clients — some older clients may not render them.",
  },
  {
    question: "How does the score work?",
    answer:
      "The score starts at 100 and deductions are made for: spam trigger words (-15 each, up to -50), all-caps words (-15), excessive punctuation (-10), too many exclamation marks (-5), too many emojis (-10), and subject lines that are too short or too long. The goal is to give you a quick benchmark — always A/B test your subject lines with real audiences for best results.",
  },
];

export default function EmailSubjectLineTesterClient() {
  const [subject, setSubject] = useState("");

  const analysis = useMemo(() => {
    if (!subject) return null;
    return analyzeSubject(subject);
  }, [subject]);

  return (
    <div className="container px-4 py-12 mx-auto max-w-4xl">
      <ToolPageSchema
        name="Email Subject Line Tester"
        description="Test your email subject lines for spam triggers, character count, emoji detection, and mobile preview. Score your subject line 0-100 before you send."
        url="https://plugmonkey.xyz/tool/email-subject-line-tester/"
        breadcrumbs={[
          { name: "Home", url: "https://plugmonkey.xyz/" },
          { name: "Free Tools", url: "https://plugmonkey.xyz/tools/" },
          {
            name: "Email Subject Line Tester",
            url: "https://plugmonkey.xyz/tool/email-subject-line-tester/",
          },
        ]}
      />

      <div className="flex items-center gap-4 mb-8 mt-6">
        <div className="p-3 bg-primary/10 rounded-full text-primary">
          <HiMail className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">Email Subject Line Tester</h1>
          <p className="text-muted-foreground mt-1">
            Score your subject line before you hit send. Check for spam words, length, and mobile
            preview.
          </p>
        </div>
      </div>

      <div className="mb-8 text-muted-foreground leading-relaxed space-y-3 max-w-3xl">
        <p>
          Your subject line is the single biggest factor in whether your email gets opened or
          ignored. A great product or offer means nothing if recipients delete your email before
          reading it.
        </p>
        <p>
          This tool analyzes your subject line in real time: checking for known spam trigger words
          that cause email clients to flag your message, detecting all-caps and excessive
          punctuation, showing a mobile inbox preview (truncated at ~40 characters), and giving you
          an overall score from 0 to 100 so you can iterate fast.
        </p>
      </div>

      {/* Input */}
      <div className="space-y-2 mb-8">
        <Label htmlFor="subject-input" className="text-base font-semibold">
          Your Subject Line
        </Label>
        <Input
          id="subject-input"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Enter your email subject line here..."
          className="text-lg h-12"
          maxLength={200}
        />
        <p className="text-xs text-muted-foreground">{subject.length} characters</p>
      </div>

      {/* Results */}
      {analysis && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Score */}
          <Card className="bg-primary/5 border-primary/20 shadow-sm">
            <CardHeader>
              <CardTitle className="text-center text-base">Overall Score</CardTitle>
            </CardHeader>
            <CardContent className="flex justify-center pb-6">
              <ScoreRing score={analysis.score} />
            </CardContent>
          </Card>

          {/* Checks */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="text-base">Analysis</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <CheckItem
                pass={analysis.isIdealLength}
                warn={!analysis.isIdealLength && analysis.len >= 10 && analysis.len <= 70}
                label={
                  analysis.len === 0
                    ? "No subject line entered"
                    : analysis.isIdealLength
                      ? `Ideal length (${analysis.len} chars, target 30–50)`
                      : analysis.len < 30
                        ? `Too short (${analysis.len} chars) — aim for 30–50`
                        : analysis.len > 70
                          ? `Too long (${analysis.len} chars) — may be cut off`
                          : `Acceptable length (${analysis.len} chars)`
                }
              />

              <CheckItem
                pass={analysis.foundSpamWords.length === 0}
                label={
                  analysis.foundSpamWords.length === 0
                    ? "No spam trigger words detected"
                    : `Spam words found: ${analysis.foundSpamWords.slice(0, 3).join(", ")}${analysis.foundSpamWords.length > 3 ? ` +${analysis.foundSpamWords.length - 3} more` : ""}`
                }
              />

              <CheckItem
                pass={!analysis.hasAllCaps}
                label={
                  analysis.hasAllCaps
                    ? `All-caps words detected: ${analysis.allCapsWords.slice(0, 3).join(", ")}`
                    : "No all-caps words"
                }
              />

              <CheckItem
                pass={!analysis.excessivePunctuation && analysis.exclamationCount <= 1}
                warn={analysis.exclamationCount === 1}
                label={
                  analysis.excessivePunctuation
                    ? "Excessive punctuation (!! or ??) detected"
                    : analysis.exclamationCount > 1
                      ? `${analysis.exclamationCount} exclamation marks — use sparingly`
                      : "Punctuation looks fine"
                }
              />

              <CheckItem
                pass={!analysis.hasEmoji || analysis.emojiCount <= 2}
                warn={analysis.hasEmoji && analysis.emojiCount === 1}
                label={
                  !analysis.hasEmoji
                    ? "No emojis (consider adding 1 for standout)"
                    : analysis.emojiCount <= 2
                      ? `${analysis.emojiCount} emoji${analysis.emojiCount > 1 ? "s" : ""} detected — use with care`
                      : `${analysis.emojiCount} emojis — too many, may trigger spam filters`
                }
              />

              <CheckItem
                pass={!analysis.isTruncated}
                warn={analysis.isTruncated}
                label={
                  analysis.isTruncated
                    ? `Will be cut off on mobile after ~40 chars`
                    : "Full subject visible on mobile"
                }
              />
            </CardContent>
          </Card>

          {/* Mobile Preview */}
          <Card className="md:col-span-3">
            <CardHeader>
              <CardTitle className="text-base">Mobile Inbox Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-muted rounded-xl p-4 max-w-sm mx-auto">
                {/* Fake phone UI */}
                <div className="bg-background rounded-lg p-3 shadow-sm border border-border">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex-shrink-0 flex items-center justify-center">
                      <HiMail className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-baseline mb-0.5">
                        <span className="text-xs font-semibold truncate">Sender Name</span>
                        <span className="text-xs text-muted-foreground ml-2 flex-shrink-0">
                          Now
                        </span>
                      </div>
                      <p className="text-sm font-medium truncate leading-tight">
                        {analysis.mobilePreview || "Your subject line preview"}
                      </p>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        Preview of email body text would appear here...
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {analysis.isTruncated && (
                <p className="text-center text-xs text-yellow-600 dark:text-yellow-400 mt-2">
                  Subject is truncated on mobile — the bold text above is what recipients see first.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {!analysis && (
        <div className="rounded-xl border-2 border-dashed border-border p-12 text-center text-muted-foreground mb-8">
          <HiMail className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>Type your subject line above to see the analysis.</p>
        </div>
      )}

      {/* FAQ Section */}
      <section className="mt-16 pt-12 border-t border-border">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-border bg-card p-8 md:p-12 shadow-sm">
            <div className="flex items-center gap-4 mb-10">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <HiQuestionMarkCircle className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
                <p className="text-muted-foreground text-sm">
                  Click on a question to view the answer.
                </p>
              </div>
            </div>
            <UnifiedFAQ items={faqItems} showSupportLink={false} className="space-y-4" />
          </div>
        </div>
      </section>

      <ToolsCustomDevCTA />
    </div>
  );
}
